import React, { ReactNode } from "react";
import styled from "styled-components";
import { motion, HTMLMotionProps } from "framer-motion";

/**
 * プロジェクトメンバーの「グループ枠」（クライアント / プロデューサー /
 * スペシャリスト）。プロジェクトバナーのユーザーリストと、管理画面の
 * メンバーシップモーダルで同じ見た目を共有するためのグローバル部品。
 *
 * motion.div ベースなので、バナーのように `initial` / `animate` / `exit` /
 * `transition` を渡せば展開アニメーションが付く（未指定なら静的表示）。
 */
export type MemberGroupVariant = "client" | "producer" | "specialist";

const GROUP_PRESETS: Record<
  MemberGroupVariant,
  { background: string; headingClass: string; defaultTitle: string }
> = {
  client: {
    background: "var(--soremo-blue)",
    headingClass: "heading-16 u-line-height-150 u-text-white",
    defaultTitle: "クライアント",
  },
  producer: {
    background: "var(--soremo-light-gray)",
    headingClass: "heading-16 u-line-height-150 u-text-white",
    defaultTitle: "プロデューサー",
  },
  specialist: {
    background: "var(--soremo-border)",
    headingClass: "heading-16 u-line-height-150",
    defaultTitle: "スペシャリスト",
  },
};

/** スペシャリストグループ既定の「クライアント非公開」注記 */
const SpecialistNote: React.FC = () => (
  <div className="bodytext-11 u-row u-gap4 u-text-light-gray">
    <span className="material-symbols-rounded" style={{ fontSize: "14px" }}>
      visibility_off
    </span>
    クライアント非公開
  </div>
);

const GroupContainer = styled(motion.div)<{ $background: string }>`
  width: 100%;
  display: flex;
  padding: 16px 8px;
  flex-direction: column;
  gap: 8px;
  background: ${({ $background }) => $background};
  border-radius: 4px;
`;

export interface MemberGroupSectionProps
  extends Omit<HTMLMotionProps<"div">, "title"> {
  variant: MemberGroupVariant;
  /** 見出し。省略時はバリアント既定（クライアント / プロデューサー / スペシャリスト）。 */
  title?: ReactNode;
  /**
   * 見出し右側の注記。specialist は省略時に「クライアント非公開」を表示する
   * （`null` を渡すと非表示）。
   */
  note?: ReactNode;
  children: ReactNode;
}

const MemberGroupSection: React.FC<MemberGroupSectionProps> = ({
  variant,
  title,
  note,
  children,
  ...motionProps
}) => {
  const preset = GROUP_PRESETS[variant];
  const resolvedNote =
    note !== undefined ? note : variant === "specialist" ? <SpecialistNote /> : null;

  return (
    <GroupContainer $background={preset.background} {...motionProps}>
      {resolvedNote ? (
        <div className="u-row-between">
          <div className={preset.headingClass}>
            {title ?? preset.defaultTitle}
          </div>
          {resolvedNote}
        </div>
      ) : (
        <div className={preset.headingClass}>{title ?? preset.defaultTitle}</div>
      )}
      {children}
    </GroupContainer>
  );
};

export default MemberGroupSection;
